import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack } from '@mui/material';
import { earlyAccumulationAPI } from '../services/api';
import BottomingFlowSkeleton from '../components/bottomingFlow/BottomingFlowSkeleton';
import MarketInternalsPanel from '../components/bottomingFlow/MarketInternalsPanel';
import PageHeader from '../components/bottomingFlow/PageHeader';
import SideRail from '../components/bottomingFlow/SideRail';
import WatchlistTable from '../components/bottomingFlow/WatchlistTable';
import { toneToColor } from '../components/bottomingFlow/shared';

export default function BottomingFlowMonitor() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [summary, setSummary] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [scoreFloor, setScoreFloor] = useState(60);
  const [strongOnly, setStrongOnly] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    setHistoryError('');
    try {
      const [summaryRes, candidatesRes, historyRes] = await Promise.allSettled([
        earlyAccumulationAPI.getMonitorSummary({
          min_score: scoreFloor,
          strong_only: strongOnly,
          score_model: 'continuous_bottom',
          refresh_if_stale: true,
        }),
        earlyAccumulationAPI.getCandidates({
          min_score: scoreFloor,
          score_model: 'continuous_bottom',
          early_only: false,
          limit: 80,
          strong_only: strongOnly,
          lifecycle_filter: stateFilter,
          refresh_if_stale: true,
        }),
        earlyAccumulationAPI.getHistory({
          days: 12,
          min_score: scoreFloor,
          strong_only: strongOnly,
          score_model: 'continuous_bottom',
          refresh_if_stale: true,
        }),
      ]);

      if (summaryRes.status !== 'fulfilled') {
        throw summaryRes.reason;
      }
      if (candidatesRes.status !== 'fulfilled') {
        throw candidatesRes.reason;
      }

      setSummary(summaryRes.value.data);
      setCandidates(candidatesRes.value.data?.candidates || []);

      if (historyRes.status === 'fulfilled') {
        setHistory(historyRes.value.data?.history || []);
      } else {
        setHistory([]);
        setHistoryError(historyRes.reason?.response?.data?.detail || historyRes.reason?.message || t('bottomingFlow.errors.historyFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('bottomingFlow.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [scoreFloor, stateFilter, strongOnly, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const marketBiasMeta = useMemo(() => {
    const read = summary?.market_internal?.read || {};
    return {
      color: toneToColor(read.tone),
      label: read.label_key ? t(read.label_key) : '-',
    };
  }, [summary, t]);

  const internalBreadth = summary?.internal_breadth || {};
  const indexContext = summary?.index_context || {};

  return (
    <Box sx={{ py: { xs: 1.5, md: 3 } }}>
      {loading && !summary ? (
        <BottomingFlowSkeleton />
      ) : (
        <Stack spacing={1.5}>
          <PageHeader
            t={t}
            summary={summary}
            loading={loading}
            marketBiasMeta={marketBiasMeta}
            onRefresh={loadData}
          />

          <MarketInternalsPanel
            t={t}
            summary={summary}
            internalBreadth={internalBreadth}
            indexContext={indexContext}
          />

          {error ? <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert> : null}
          {historyError && !loading ? (
            <Alert severity="warning" sx={{ borderRadius: 1.5, py: 0.5, bgcolor: '#fffaf0', border: '1px solid #fde7c7', color: '#9a3412' }}>
              {t('bottomingFlow.history.warning')}
            </Alert>
          ) : null}

          {loading ? (
            <BottomingFlowSkeleton />
          ) : (
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.4}>
              <WatchlistTable
                t={t}
                lang={lang}
                summary={summary}
                candidates={candidates}
                scoreFloor={scoreFloor}
                setScoreFloor={setScoreFloor}
                strongOnly={strongOnly}
                setStrongOnly={setStrongOnly}
                stateFilter={stateFilter}
                setStateFilter={setStateFilter}
              />
              <SideRail
                t={t}
                lang={lang}
                summary={summary}
                history={history}
                historyError={historyError}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}
